class Carousel extends HTMLElement {
  #splide = null;
  #controller = null;
  #breakpointKey = null;
  #config = null;
  #refs = null;

  connectedCallback() {
    this.#controller = new AbortController();
    if (typeof Splide !== 'undefined') {
      this.#start();
    } else {
      window.addEventListener('load', () => this.#start(), {
        once: true,
        signal: this.#controller.signal,
      });
    }
  }

  disconnectedCallback() {
    this.#splide?.destroy();
    this.#splide = null;
    this.#controller?.abort();
    this.#controller = null;
    this.#breakpointKey = null;
    this.#config = null;
    this.#refs = null;
  }

  #int(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  #start() {
    const script = this.querySelector('.js-carousel-config');
    if (!script) return;
    try {
      this.#config = JSON.parse(script.textContent);
    } catch {
      return;
    }

    this.#refs = {
      root: this.querySelector('.carousel__splide'),
      prev: this.querySelector('.js-carousel-prev'),
      next: this.querySelector('.js-carousel-next'),
      current: this.querySelector('.js-carousel-current'),
      total: this.querySelector('.js-carousel-total'),
      progress: this.querySelector('.js-carousel-progress'),
      progressThumb: this.querySelector('.js-carousel-progress-thumb'),
    };

    const { signal } = this.#controller;

    this.#refs.prev?.addEventListener('click', () => this.#splide?.go('<'), { signal });
    this.#refs.next?.addEventListener('click', () => this.#splide?.go('>'), { signal });

    for (const query of ['(min-width: 750px)', '(min-width: 990px)']) {
      window.matchMedia(query).addEventListener('change', () => this.#sync(), { signal });
    }

    this.#sync();
  }

  #breakpoint() {
    if (window.matchMedia('(min-width: 990px)').matches) return 'desktop';
    if (window.matchMedia('(min-width: 750px)').matches) return 'tablet';
    return 'mobile';
  }

  #sync() {
    const key = this.#breakpoint();
    if (key === this.#breakpointKey && this.#splide) return;
    this.#breakpointKey = key;
    this.#build(key);
  }

  #optionsFor(key) {
    const c = this.#config;
    const bp = c[key];
    const isCenter = bp.focus === 'center';
    const alignLastToEdge = c.alignLastToEdge === true;
    const type = bp.type === 'loop' ? 'loop' : 'slide';
    const perPage = this.#int(bp.perPage, 1);
    const sides = isCenter ? 2 : 1;
    const gap = bp.gap || '0px';

    // peek = fraction of ONE slide to reveal (percent). Use fixedWidth when
    // the last slide must reach the track edge, because Splide preserves
    // padding at both ends.
    const f = (parseFloat(bp.peek) || 0) / 100;
    let padding = 0;
    let fixedWidth = null;
    if (f > 0) {
      const denominator = perPage + sides * f;

      if (alignLastToEdge && type === 'slide') {
        const width = (100 / denominator).toFixed(3);
        const gapOffset = ((parseFloat(gap) || 0) * (perPage - 1)) / perPage;
        fixedWidth = gapOffset > 0 ? `calc(${width}% - ${gapOffset.toFixed(3)}px)` : `${width}%`;
      } else {
        const peekValue = `${((f / denominator) * 100).toFixed(3)}%`;
        padding = isCenter ? peekValue : { left: 0, right: peekValue };
      }
    }

    const options = {
      type,
      perPage,
      padding,
      gap,
      arrows: false,
      pagination: false,
      drag: c.drag !== false,
      speed: this.#int(c.speed, 400),
    };

    if (fixedWidth) {
      options.fixedWidth = fixedWidth;
    }

    if (isCenter) {
      options.focus = 'center';
    }

    // Native Splide: focus + omitEnd + trimSpace lets the last page sit at the
    // track edge instead of leaving trailing empty space.
    if (alignLastToEdge && options.type !== 'loop') {
      if (!isCenter) options.focus = 0;
      options.omitEnd = true;
      options.trimSpace = true;
    }

    return options;
  }

  #build(key) {
    const { root } = this.#refs;
    if (!root || typeof Splide === 'undefined') return;

    const index = this.#splide ? this.#splide.index : 0;
    this.#splide?.destroy();

    const options = this.#optionsFor(key);
    this.#splide = new Splide(root, options);
    this.#splide.on('mounted move updated', () => this.#updateUI());
    this.#splide.mount();
    this.classList.add('carousel--ready');

    if (index > 0) this.#splide.go(Math.min(index, this.#splide.length - 1));
  }

  #updateUI() {
    const { prev, next, current, total, progress, progressThumb } = this.#refs;
    const { Controller } = this.#splide.Components;
    const idx = this.#splide.index;
    const len = this.#splide.length;
    const perPage = this.#splide.options.perPage;
    const end = Controller.getEnd();

    // Mirror Splide's own Pagination component: in focus mode every position
    // is a page, otherwise pages are perPage-sized with the end index mapped
    // to the last page by Controller.toPage.
    const pageCount = Controller.hasFocus() ? end + 1 : Math.ceil(len / perPage);
    const pageIndex = Math.min(Controller.toPage(idx), Math.max(pageCount - 1, 0));

    if (current) current.textContent = pageIndex + 1;
    if (total) total.textContent = pageCount;

    if (progressThumb) {
      const pages = Math.max(pageCount, 1);
      const style = this.#config?.paginationStyle;

      if (style === 'progress_segment') {
        this.style.setProperty('--carousel-progress-pages', String(pages));
        this.style.setProperty('--carousel-progress-index', String(pageIndex));
      } else {
        const ratio = (pageIndex + 1) / pages;
        this.style.setProperty('--carousel-progress-ratio', String(ratio));
      }
    }

    if (progress) {
      const pages = Math.max(pageCount, 1);
      // Hide only when every slide fits on one page (nothing to paginate)
      progress.hidden = pages <= 1;
      progress.setAttribute('aria-valuemax', String(pages));
      progress.setAttribute('aria-valuenow', String(pageIndex + 1));
    }

    if (this.#splide.options.type === 'loop') {
      [prev, next].forEach((btn) => {
        btn?.removeAttribute('disabled');
        btn?.classList.remove('carousel__nav--disabled');
      });
      return;
    }

    const atStart = idx === 0;
    const atEnd = idx >= end;

    prev?.classList.toggle('carousel__nav--disabled', atStart);
    prev?.toggleAttribute('disabled', atStart);
    next?.classList.toggle('carousel__nav--disabled', atEnd);
    next?.toggleAttribute('disabled', atEnd);
  }
}

if (!customElements.get('theme-carousel')) {
  customElements.define('theme-carousel', Carousel);
}
